import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import i18n from '../../i18n';
import LessonTemporaryCardCell from '../../containers/GroupSchedulePage/LessonTemporaryCardCell';
import { places } from '../../constants/places';
import { FORM_GROUP_LABEL } from '../../constants/translationLabels/formElements';

import './CalendarSchedule.scss';
import {
    findDayData,
    groupSlotCards,
    nonEmptyClasses,
    parityCardsFor,
    visibleCalendarDays,
} from './calendarScheduleData';

const shortid = require('shortid');

// ─── Sub-components ─────────────────────────────────────────────

const CalendarScheduleHeader = ({ groupList }) => (
    <TableHead>
        <TableRow>
            <TableCell colSpan={2}>{i18n.t(FORM_GROUP_LABEL)}</TableCell>
            {groupList.map(({ title }) => (
                <TableCell key={shortid.generate()} className="groupLabelCell">
                    {title}
                </TableCell>
            ))}
        </TableRow>
    </TableHead>
);

const CalendarGroupCells = ({ groups, dayName }) =>
    groupSlotCards(groups).map(({ card, span }) => (
        <TableCell
            key={shortid.generate()}
            colSpan={span}
            className={span > 1 ? 'lesson grouped' : 'lesson'}
        >
            <LessonTemporaryCardCell card={card} day={dayName} place={places.TOGETHER} />
        </TableCell>
    ));

const CalendarDayRows = ({ calendarDay, dayData, cardsFor }) => {
    const classes = nonEmptyClasses(calendarDay, dayData, cardsFor);

    if (classes.length === 0) return null;

    return classes.map((classItem, classIndex) => (
        <TableRow
            key={shortid.generate()}
            className={classIndex === 0 ? 'calendar-day-first-row' : ''}
        >
            {classIndex === 0 && (
                <TableCell rowSpan={classes.length} className="calendar-day-cell">
                    <span className="calendar-day-name">
                        {i18n.t(`common:day_of_week_short_${calendarDay.dayName}`)}
                    </span>
                    <span className="calendar-day-date">{calendarDay.dateFormatted}</span>
                </TableCell>
            )}
            <TableCell className="classNameCell calendar-class-cell">
                <span className="calendar-class-name">{classItem.class.class_name}</span>
                <span className="calendar-class-time">
                    {classItem.class.startTime} - {classItem.class.endTime}
                </span>
            </TableCell>
            <CalendarGroupCells
                groups={cardsFor(calendarDay, classItem)}
                dayName={calendarDay.dayName}
            />
        </TableRow>
    ));
};

// ─── Main component (TABLE ONLY) ────────────────────────────────

const CalendarSchedule = ({ fullSchedule, viewMode, t, cardsFor }) => {
    const { semester, groupList, resultArray } = fullSchedule;
    const cards = cardsFor ?? parityCardsFor(semester.startDay);

    const nonEmptyDays = visibleCalendarDays(fullSchedule, viewMode, cards);

    if (nonEmptyDays.length === 0) {
        return (
            <p className="empty_schedule">
                {viewMode === 'today'
                    ? t('common:no_classes_today', 'Сьогодні немає занять')
                    : t('common:empty_schedule')}
            </p>
        );
    }

    return (
        <TableContainer>
            <Table aria-label="calendar schedule">
                <CalendarScheduleHeader groupList={groupList} />
                <TableBody>
                    {nonEmptyDays.map((calendarDay) => (
                        <CalendarDayRows
                            key={calendarDay.dateFormatted}
                            calendarDay={calendarDay}
                            dayData={findDayData(resultArray, calendarDay)}
                            cardsFor={cards}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CalendarSchedule;